import { Cartesian3 } from "cesium";
// Constants for curve-aware direction calculation
const DEGENERATE_SEGMENT_THRESHOLD = 1e-10;
/**
 * Prepare centerline data with pre-computed cumulative distances
 *
 * @param pathPoints - Array of path point information from pathInfo
 * @returns Pre-computed centerline data
 */
export function prepareCenterlineData(pathPoints) {
    if (pathPoints.length < 2) {
        throw new Error("CenterlineInterpolator: At least 2 path points are required");
    }
    const positions = pathPoints.map((p) => p.position);
    const cumulativeDistances = [0];
    // Calculate cumulative distances
    for (let i = 1; i < positions.length; i++) {
        const distance = Cartesian3.distance(positions[i - 1], positions[i]);
        cumulativeDistances.push(cumulativeDistances[i - 1] + distance);
    }
    const totalLength = cumulativeDistances[cumulativeDistances.length - 1];
    return {
        positions,
        cumulativeDistances,
        totalLength,
    };
}
/**
 * Interpolate position and direction at a specific distance along the centerline
 *
 * Uses linear interpolation for both position and direction.
 *
 * @param centerlineData - Pre-computed centerline data
 * @param targetDistance - Distance along the centerline (0 to totalLength)
 * @returns Interpolation result
 */
export function interpolateAt(centerlineData, targetDistance) {
    const { positions, cumulativeDistances, totalLength } = centerlineData;
    // Clamp target distance to valid range
    const clampedDistance = Math.max(0, Math.min(targetDistance, totalLength));
    // Find the segment containing the target distance using binary search
    let segmentIndex = 0;
    for (let i = 0; i < cumulativeDistances.length - 1; i++) {
        if (clampedDistance <= cumulativeDistances[i + 1]) {
            segmentIndex = i;
            break;
        }
    }
    // Handle edge case: target is at or beyond the last point
    if (segmentIndex >= positions.length - 1) {
        segmentIndex = positions.length - 2;
    }
    // Calculate local interpolation parameter (0-1)
    const segmentStartDist = cumulativeDistances[segmentIndex];
    const segmentEndDist = cumulativeDistances[segmentIndex + 1];
    const segmentLength = segmentEndDist - segmentStartDist;
    const localT = segmentLength > 0
        ? (clampedDistance - segmentStartDist) / segmentLength
        : 0;
    // Interpolate position
    const p0 = positions[segmentIndex];
    const p1 = positions[segmentIndex + 1];
    const position = Cartesian3.lerp(p0, p1, localT, new Cartesian3());
    // Calculate direction from the segment tangent (not interpolation of direction vectors)
    // This ensures the direction is always perpendicular to the actual path
    let tangent = Cartesian3.subtract(p1, p0, new Cartesian3());
    Cartesian3.normalize(tangent, tangent);
    // Special handling for segment boundary points (where localT ≈ 0 or 1)
    // Blend with adjacent segment's direction for smoother transition
    const epsilon = 0.001;
    if (localT < epsilon && segmentIndex > 0) {
        // At start of segment: blend with previous segment
        const p_prev = positions[segmentIndex - 1];
        const prevTangent = Cartesian3.subtract(p0, p_prev, new Cartesian3());
        Cartesian3.normalize(prevTangent, prevTangent);
        // Average the two tangents
        tangent = Cartesian3.add(prevTangent, tangent, new Cartesian3());
        Cartesian3.normalize(tangent, tangent);
    }
    else if (localT > 1 - epsilon && segmentIndex < positions.length - 2) {
        // At end of segment: blend with next segment
        const p2 = positions[segmentIndex + 2];
        const nextTangent = Cartesian3.subtract(p2, p1, new Cartesian3());
        Cartesian3.normalize(nextTangent, nextTangent);
        // Average the two tangents
        tangent = Cartesian3.add(tangent, nextTangent, new Cartesian3());
        Cartesian3.normalize(tangent, tangent);
    }
    // Calculate right direction (cross product with up vector)
    const up = new Cartesian3(0, 0, 1);
    const right = Cartesian3.cross(tangent, up, new Cartesian3());
    Cartesian3.normalize(right, right);
    // Calculate other direction vectors
    const left = Cartesian3.negate(right, new Cartesian3());
    const backward = Cartesian3.negate(tangent, new Cartesian3());
    const down = new Cartesian3(0, 0, -1);
    const direction = {
        forward: tangent,
        right: right,
        left: left,
        up: up,
        down: down,
        backward: backward,
    };
    return {
        position,
        direction,
        segmentIndex,
        localT,
    };
}
/**
 * Find the nearest point on the centerline to a given position
 *
 * Searches all segments and returns the closest point along with its parameters.
 *
 * @param centerlineData - Pre-computed centerline data
 * @param queryPosition - Position to find nearest point to
 * @returns Nearest point result
 */
export function findNearestPointOnCenterline(centerlineData, queryPosition) {
    const { positions, cumulativeDistances } = centerlineData;
    let minDistance = Number.POSITIVE_INFINITY;
    let bestSegmentIndex = 0;
    let bestLocalT = 0;
    let bestPointOnSegment = new Cartesian3();
    // Check each segment
    for (let i = 0; i < positions.length - 1; i++) {
        const p0 = positions[i];
        const p1 = positions[i + 1];
        // Find the closest point on this line segment
        const result = closestPointOnSegment(queryPosition, p0, p1);
        if (result.distance < minDistance) {
            minDistance = result.distance;
            bestSegmentIndex = i;
            bestLocalT = result.t;
            bestPointOnSegment = result.point;
        }
    }
    // Calculate distance along centerline
    const segmentStartDist = cumulativeDistances[bestSegmentIndex];
    const segmentEndDist = cumulativeDistances[bestSegmentIndex + 1];
    const segmentLength = segmentEndDist - segmentStartDist;
    const distanceAlongCenterline = segmentStartDist + bestLocalT * segmentLength;
    // Calculate direction from the segment tangent
    const p0 = positions[bestSegmentIndex];
    const p1 = positions[bestSegmentIndex + 1];
    let tangent = Cartesian3.subtract(p1, p0, new Cartesian3());
    Cartesian3.normalize(tangent, tangent);
    // Special handling for segment boundary points (where bestLocalT ≈ 0 or 1)
    // Blend with adjacent segment's direction for smoother transition
    const epsilon = 0.001;
    if (bestLocalT < epsilon && bestSegmentIndex > 0) {
        // At start of segment: blend with previous segment
        const p_prev = positions[bestSegmentIndex - 1];
        const prevTangent = Cartesian3.subtract(p0, p_prev, new Cartesian3());
        Cartesian3.normalize(prevTangent, prevTangent);
        // Average the two tangents
        tangent = Cartesian3.add(prevTangent, tangent, new Cartesian3());
        Cartesian3.normalize(tangent, tangent);
    }
    else if (bestLocalT > 1 - epsilon &&
        bestSegmentIndex < positions.length - 2) {
        // At end of segment: blend with next segment
        const p2 = positions[bestSegmentIndex + 2];
        const nextTangent = Cartesian3.subtract(p2, p1, new Cartesian3());
        Cartesian3.normalize(nextTangent, nextTangent);
        // Average the two tangents
        tangent = Cartesian3.add(tangent, nextTangent, new Cartesian3());
        Cartesian3.normalize(tangent, tangent);
    }
    // Calculate right direction (cross product with up vector)
    const up = new Cartesian3(0, 0, 1);
    const right = Cartesian3.cross(tangent, up, new Cartesian3());
    Cartesian3.normalize(right, right);
    // Calculate other direction vectors
    const left = Cartesian3.negate(right, new Cartesian3());
    const backward = Cartesian3.negate(tangent, new Cartesian3());
    const down = new Cartesian3(0, 0, -1);
    const direction = {
        forward: tangent,
        right: right,
        left: left,
        up: up,
        down: down,
        backward: backward,
    };
    return {
        position: bestPointOnSegment,
        direction,
        distanceAlongCenterline,
        segmentIndex: bestSegmentIndex,
        localT: bestLocalT,
        distanceFromQuery: minDistance,
    };
}
/**
 * Find the closest point on a line segment to a query point
 *
 * @param query - Query point
 * @param p0 - Start of line segment
 * @param p1 - End of line segment
 * @returns Closest point, parameter t, and distance
 */
export function closestPointOnSegment(query, p0, p1) {
    const segment = Cartesian3.subtract(p1, p0, new Cartesian3());
    const segmentLengthSq = Cartesian3.magnitudeSquared(segment);
    // Handle degenerate case: p0 and p1 are the same
    if (segmentLengthSq < DEGENERATE_SEGMENT_THRESHOLD) {
        return {
            point: Cartesian3.clone(p0),
            t: 0,
            distance: Cartesian3.distance(query, p0),
        };
    }
    const toQuery = Cartesian3.subtract(query, p0, new Cartesian3());
    const t = Math.max(0, Math.min(1, Cartesian3.dot(toQuery, segment) / segmentLengthSq));
    const closestPoint = Cartesian3.lerp(p0, p1, t, new Cartesian3());
    const distance = Cartesian3.distance(query, closestPoint);
    return { point: closestPoint, t, distance };
}
/**
 * Find the nearest path point index to a given query position
 *
 * @param queryPosition - Position to search from
 * @param pathPoints - Array of path points
 * @returns Index of the nearest path point, or -1 if pathPoints is empty
 */
export function findNearestPathPointIndex(queryPosition, pathPoints) {
    if (pathPoints.length === 0)
        return -1;
    let minDistance = Number.POSITIVE_INFINITY;
    let nearestIndex = -1;
    for (let i = 0; i < pathPoints.length; i++) {
        const pathPoint = pathPoints[i];
        const dx = pathPoint.position.x - queryPosition.x;
        const dy = pathPoint.position.y - queryPosition.y;
        const dz = pathPoint.position.z - queryPosition.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (distance < minDistance) {
            minDistance = distance;
            nearestIndex = i;
        }
    }
    return nearestIndex;
}
//# sourceMappingURL=mid-path-interpolation.js.map